// body.ts handles the PUT/POST body and Content-Type machinery shared
// across data, userdata, and capture.
//
// Rules (encoded by normalizeBody):
//   - body must be one of string | Blob | ArrayBuffer | ArrayBufferView.
//     Plain objects are rejected with invalid_argument — callers wanting
//     JSON do JSON.stringify(obj) and pass "application/json" themselves.
//   - contentType is required and is forwarded verbatim as the Content-Type
//     header. The server has the canonical allowlist; the client only
//     pre-flights the always-denied set for a clearer error than 415.
//   - When a Blob is passed whose .type differs from the explicit
//     contentType arg, the explicit arg wins and we console.warn once per
//     page. Refusing would surprise the common "I have a Blob from a file
//     input and want to relabel it" case.

import { BacklitError } from "./errors.js";
// ALWAYS_DENIED (script-bearing types the data plane rejects regardless of the
// allowlist; pre-flighted client-side to skip the round-trip on obvious
// mistakes) and COMPRESSIBLE (types eligible for gzip pre-compression before
// upload) are GENERATED from internal/contenttype so the SDK and the Go data
// plane can never drift — dropping a deny-list entry on only one side would be
// a same-origin script-injection hole. Edit internal/contenttype + run
// `task contenttypes:sync`; never edit the generated file by hand.
import { ALWAYS_DENIED, COMPRESSIBLE } from "./content-types.gen.js";

let warnedBlobTypeMismatch = false;

export interface NormalizedBody {
  body: BodyInit;
  contentType: string;
}

export function normalizeBody(
  raw: unknown,
  contentType: unknown,
): NormalizedBody {
  if (typeof contentType !== "string" || contentType.length === 0) {
    throw new BacklitError(
      "invalid_argument",
      "contentType is required and must be a non-empty string",
    );
  }
  const ct = canonicalizeContentType(contentType);
  if (ALWAYS_DENIED.has(ct)) {
    throw new BacklitError(
      "unsupported_content_type",
      `Content-Type ${ct} is not allowed by the data API (always-denied for security)`,
    );
  }

  // BodyInit guard. We accept string, Blob, ArrayBuffer, and ArrayBuffer
  // views (Uint8Array et al). FormData / URLSearchParams / Response
  // streams are deliberately out of scope for v1 — too many subtle
  // Content-Type interactions.
  if (
    typeof raw === "string" ||
    raw instanceof Blob ||
    raw instanceof ArrayBuffer ||
    ArrayBuffer.isView(raw)
  ) {
    // Empty bodies are allowed — server permits zero-length objects.
    if (raw instanceof Blob && raw.type && raw.type !== ct) {
      maybeWarnBlobTypeMismatch(raw.type, ct);
    }
    return { body: raw as BodyInit, contentType: ct };
  }

  throw new BacklitError(
    "invalid_argument",
    "body must be a string, Blob, ArrayBuffer, or ArrayBufferView. " +
      "For JSON, call JSON.stringify(obj) and pass 'application/json'.",
  );
}

// canonicalizeContentType lower-cases the type and strips parameters
// (charset, boundary). Mirrors cmd/data-api/size.go:checkContentType so
// the client and server see the same canonical string.
export function canonicalizeContentType(raw: string): string {
  let ct = raw.trim().toLowerCase();
  const semi = ct.indexOf(";");
  if (semi >= 0) {
    ct = ct.slice(0, semi).trim();
  }
  return ct;
}

// isCompressionStreamAvailable feature-detects the WHATWG Compression
// Streams API. CompressionStream landed in Chromium 80, Firefox 113,
// and Safari 16.4 — comfortably under our v1 target floor — but the
// SDK loads in arbitrary embedding contexts (file://, iframes,
// degraded test runners), so we degrade gracefully when it's
// missing: maybeGzip just passes the body through and the server
// transparently gzips compressible content-types on its end.
const COMPRESSION_AVAILABLE: boolean = (() => {
  try {
    return typeof CompressionStream !== "undefined";
  } catch {
    return false;
  }
})();

// Result of pre-upload gzip evaluation. encoding is "gzip" when the
// caller should send Content-Encoding: gzip alongside the returned
// body; null when the body is unchanged (non-compressible content-
// type, or no CompressionStream support).
export interface MaybeGzipped {
  body: BodyInit;
  encoding: "gzip" | null;
}

// maybeGzip pipes body through CompressionStream("gzip") when both
// (a) the content-type is in the compressible set and (b) the
// runtime exposes CompressionStream. Returns the original body
// otherwise — every code path stays a single allocation in the
// no-gzip case. Failures during compression fall back to the
// uncompressed body rather than throwing, so a CompressionStream
// quirk on an exotic browser doesn't break put().
export async function maybeGzip(
  body: BodyInit,
  contentType: string,
): Promise<MaybeGzipped> {
  if (!COMPRESSION_AVAILABLE || !COMPRESSIBLE.has(contentType)) {
    return { body, encoding: null };
  }
  try {
    const sourceStream = bodyToReadableStream(body);
    // TS's CompressionStream typings want a Uint8Array source even
    // though the runtime accepts any BufferSource; cast through
    // ReadableWritablePair<Uint8Array, Uint8Array> to match.
    const compressedStream = sourceStream.pipeThrough(
      new CompressionStream("gzip") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>,
    );
    // Response is the easy bridge from a ReadableStream to a Blob.
    const compressed = await new Response(compressedStream).blob();
    return { body: compressed, encoding: "gzip" };
  } catch {
    // Belt-and-braces: if anything goes wrong (unusual Blob source,
    // CompressionStream throw on edge-case input), let the server
    // handle compression instead.
    return { body, encoding: null };
  }
}

// bodyToReadableStream normalises every BodyInit variant the SDK
// accepts (string | Blob | ArrayBuffer | ArrayBufferView) into a
// ReadableStream<Uint8Array> so CompressionStream has a uniform input
// shape. Blob.stream() is preferred when available; otherwise a
// single-chunk synthetic stream over the in-memory bytes.
function bodyToReadableStream(body: BodyInit): ReadableStream<Uint8Array> {
  if (body instanceof Blob && typeof body.stream === "function") {
    return body.stream() as ReadableStream<Uint8Array>;
  }
  let bytes: Uint8Array;
  if (body instanceof Blob) {
    // No .stream() in this env — fall through to a Response detour.
    return new Response(body).body as ReadableStream<Uint8Array>;
  }
  if (typeof body === "string") {
    bytes = new TextEncoder().encode(body);
  } else if (body instanceof ArrayBuffer) {
    bytes = new Uint8Array(body.slice(0));
  } else if (ArrayBuffer.isView(body)) {
    bytes = new Uint8Array(body.buffer.slice(
      body.byteOffset,
      body.byteOffset + body.byteLength,
    ));
  } else {
    // Defensive — normalizeBody upstream already rejects everything
    // else, so we never expect to land here.
    bytes = new Uint8Array(0);
  }
  return new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
}

function maybeWarnBlobTypeMismatch(blobType: string, explicit: string): void {
  if (warnedBlobTypeMismatch) return;
  warnedBlobTypeMismatch = true;
  // Use console.warn directly; SDK consumers don't get to plug a logger.
  // eslint-disable-next-line no-console
  console.warn(
    `[backlit] Blob.type "${blobType}" overridden by explicit contentType "${explicit}"; ` +
      "the explicit argument wins. This warning fires at most once per page.",
  );
}
